import { IconFont, Input, itemBackgroundTransitionOnClick, Popover } from '@byecode/ui'
import type { DataSourceAbstract, RectSize, RectSizeUnit, VariableADTvalue } from '@lighthouse/core'
import { VariableType } from '@lighthouse/core'
import { emptyNumberField, getInputInnerTypeMatch, useAtomData, VariableSelect, type VariableTree2 } from '@lighthouse/shared'
import isDeepEqual from 'fast-deep-equal'
import React, { useCallback, useMemo, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'
import { useImmer } from 'use-immer'

import { lastPageOfStackAtom, pageInternalStateAtomFamily } from '@/atoms/page/state'
import type { NodeIdWithScope } from '@/atoms/page/types'
import { useDeviceType } from '@/hooks/useApplication'
import { transform2Integer, transform2Number } from '@/utils/number'

import { SizeIcon } from './SizeIcon'
import { convertSize } from './utils'

type MinMaxSizeInputType = 'minWidth' | 'maxWidth' | 'minHeight' | 'maxHeight'

export type MinMaxSizeInputValue = {
    size?: RectSize
    unit?: RectSizeUnit
    variable?: VariableADTvalue
}

export interface MinMaxVariableOptions {
    viewOptions?: VariableTree2[]
    pageOptions?: VariableTree2[]
    userOption?: VariableTree2
    dataSourceList?: DataSourceAbstract[]
}

interface MinMaxSizeInputProps {
    type: MinMaxSizeInputType
    value?: MinMaxSizeInputValue
    disable?: boolean
    onChange: (value: MinMaxSizeInputValue) => void
    onRemove: () => void
    variableOptions?: MinMaxVariableOptions
}

type MinMaxSizeUnit = 'px' | '%' | 'vw' | 'vh' | 'rem'

const cssUnit = new Set<MinMaxSizeUnit>(['px', '%', 'vw', 'vh', 'rem'])

const SCxRectMinMaxSizeInputWrapper = styled.div`
    display: flex;
    width: 180px;
`

const SCxVariableSelectWrapper = styled.div`
    flex: 1;
    min-width: 0;
    overflow: hidden;
`

const SCxDropdownButton = styled.div`
    display: flex;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    background-color: var(--color-gray-100);
    border-left: 1px solid var(--color-gray-200);
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
    cursor: pointer;

    ${itemBackgroundTransitionOnClick}

    &:hover {
        background-color: var(--color-gray-200);
    }
`

const SCxRemoveButton = styled.div`
    display: flex;
    width: 32px;
    height: 32px;
    margin-left: 8px;
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    border-radius: 6px;
    background-color: var(--color-gray-100);
    cursor: pointer;

    ${itemBackgroundTransitionOnClick}

    &:hover {
        background-color: var(--color-gray-200);
    }
`

const SCxListItem = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 8px 16px;
    cursor: pointer;

    &:hover {
        background-color: var(--color-gray-100);
    }
`

export const RectMinMaxSizeInput: React.FC<MinMaxSizeInputProps> = ({
    type,
    value,
    disable,
    onChange,
    onRemove,
    variableOptions
}) => {
    const previewType = useDeviceType()
    const [dropdownOpened, setDropdownOpened] = useState(false)
    const init = useMemo(() => value ?? { size: undefined, unit: undefined }, [value])

    const [v, setV] = useImmer<MinMaxSizeInputValue>(init)
    const lowerCaseType = type.toLowerCase()
    const inputIconType = useMemo(() => {
        if (lowerCaseType.includes('width')) {
            return 'Width'
        }
        if (lowerCaseType.includes('height')) {
            return 'Height'
        }
    }, [lowerCaseType])
    const dropdownOptions = useMemo(() => {
        return [
            { label: '固定值', value: 'px' },
            { label: '百分比', value: '%' },
            { label: '相对视窗宽度', value: 'vw' },
            { label: '相对视窗高度', value: 'vh' },
            { label: '相对根元素', value: 'rem' }
        ].filter(({ value }) => {
            if (lowerCaseType.includes('width')) {
                return value !== 'vh'
            }
            if (lowerCaseType.includes('height')) {
                return value !== 'vw'
            }
            return true
        })
    }, [lowerCaseType])

    const stackId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.stackId || '', [])
    )
    const blockId = useAtomData(
        pageInternalStateAtomFamily(stackId),
        useCallback(s => s?.selectedNodes?.[0], [])
    )

    useUpdateEffect(() => {
        if (isDeepEqual(init, v)) {
            return
        }
        setV(init)
    }, [value])

    // 将旧格式（size: number）或 VALUE 变量统一转为 VariableADTvalue 供 VariableSelect 展示
    const variableValue = useMemo((): VariableADTvalue | undefined => {
        if (v.variable) return v.variable
        if (typeof v.size === 'number') {
            return {
                type: VariableType.VALUE,
                valueVariable: { type: 'number', value: String(v.size) }
            }
        }
        return undefined
    }, [v.variable, v.size])

    const handleVariableChange = useCallback(
        (variable: VariableADTvalue) => {
            const val: MinMaxSizeInputValue = {
                size: undefined,
                unit: value?.unit || 'px',
                variable
            }
            setV(val)
            if (isDeepEqual(value, val)) {
                return
            }
            onChange(val)
        },
        [onChange, setV, value]
    )

    // 当前数值（用于 SizeIcon 拖拽起点回退与显示）
    const currentSizeNumber = useMemo(() => {
        if (v.variable?.type === VariableType.VALUE) {
            const raw = v.variable.valueVariable?.value
            const num = transform2Number(Number(raw).toFixed(2))
            return Number.isNaN(num) ? undefined : num
        }
        return typeof v.size === 'number' ? v.size : undefined
    }, [v.variable, v.size])

    // px/%/vw/vh/rem：以当前数值为起点按 px 偏移拖拽（不传 blockStyleSize）；无单位：以 DOM px 为起点
    const sizeIconBlockStyleSize = useMemo<{ blockId: NodeIdWithScope | undefined; sizeInputType: MinMaxSizeInputType } | undefined>(() => {
        if (v?.unit && cssUnit.has(v.unit)) {
            return undefined
        }
        return { blockId, sizeInputType: type }
    }, [v?.unit, blockId, type])

    // 拖拽产生的数值 → 转为 VALUE 变量上报；cssUnit 单位保留当前单位，其余回退为 px
    const handleMoveChange = useCallback(
        (num: number) => {
            const rounded = transform2Integer(num)
            const unit: RectSizeUnit = value?.unit && cssUnit.has(value.unit) ? value.unit : 'px'
            const variable: VariableADTvalue = {
                type: VariableType.VALUE,
                valueVariable: { type: 'number', value: String(rounded) }
            }
            const val: MinMaxSizeInputValue = { size: undefined, unit, variable }
            setV(val)
            if (isDeepEqual(val, value)) {
                return
            }
            onChange(val)
        },
        [onChange, setV, value]
    )

    const handleItemClick = useCallback(
        (sizeOrUnit: MinMaxSizeUnit) => {
            if (!blockId) {
                return
            }
            const size = convertSize(blockId, type, sizeOrUnit, previewType) as RectSize
            // 仅当绑定的是真正的变量引用时保留绑定；VALUE 字面量（来自输入/拖拽）需按新单位重算
            const currentVariable = v.variable
            const keepBinding = currentVariable && currentVariable.type !== VariableType.VALUE
            const val: MinMaxSizeInputValue = keepBinding
                ? { size: undefined, unit: sizeOrUnit, variable: currentVariable }
                : { size, unit: sizeOrUnit }
            setV(val)
            setDropdownOpened(false)
            if (isDeepEqual(val, value)) {
                return
            }
            onChange(val)
        },
        [blockId, onChange, previewType, setV, type, value, v.variable]
    )

    const handleBlur = useCallback(
        (ev: React.FocusEvent<HTMLInputElement>) => {
            const inputV = ev.target.value
            const number = transform2Number(Number(inputV).toFixed(2))
            const validNumber = Number.isNaN(number) ? undefined : number
            const val: MinMaxSizeInputValue = {
                size: validNumber,
                unit: value?.unit
            }
            setV(val)
            if (isDeepEqual(val, value)) {
                return
            }
            onChange(val)
        },
        [onChange, setV, value]
    )

    return (
        <SCxRectMinMaxSizeInputWrapper>
            <SCxVariableSelectWrapper>
                <VariableSelect
                    field={emptyNumberField}
                    value={variableValue}
                    innerTypes={getInputInnerTypeMatch('NUMBER')}
                    enableFormula
                    onChange={handleVariableChange}
                    viewOptions={variableOptions?.viewOptions}
                    pageOptions={variableOptions?.pageOptions}
                    userOption={variableOptions?.userOption}
                    dataSourceList={variableOptions?.dataSourceList}
                    disabled={disable}
                    width="100%"
                    prefix={
                        !disable && inputIconType ? (
                            <SizeIcon
                                type={inputIconType}
                                min={0}
                                value={currentSizeNumber}
                                blockStyleSize={sizeIconBlockStyleSize}
                                onChange={handleMoveChange}
                            />
                        ) : undefined
                    }
                    wrapperStyle={{
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0
                    }}
                    focusOutLine={false}
                />
            </SCxVariableSelectWrapper>
            <Popover opened={dropdownOpened} width={180} position="bottom-end" disabled={disable} onChange={setDropdownOpened}>
                <Popover.Target>
                    <SCxDropdownButton>
                        <IconFont type="ArrowDownSmall" color="var(--color-gray-400)" />
                    </SCxDropdownButton>
                </Popover.Target>
                <Popover.Dropdown>
                    {dropdownOptions.map(option => (
                        <SCxListItem key={option.value} onClick={() => handleItemClick(option.value as MinMaxSizeUnit)}>
                            {option.label}
                            {(value?.unit === option.value || value?.size === option.value) && (
                                <IconFont type="Tick" color="var(--color-main)" />
                            )}
                        </SCxListItem>
                    ))}
                </Popover.Dropdown>
            </Popover>
            <SCxRemoveButton onClick={onRemove}>
                <IconFont type="Reduce" color="var(--color-gray-400)" />
            </SCxRemoveButton>
        </SCxRectMinMaxSizeInputWrapper>
    )
}
